"use client";
import React, { useCallback } from "react";
import { ShoppingCart } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable AddToCart component for adding products to cart
 * 
 * @param {Object} props
 * @param {string} props.productId - The product ID
 * @param {Function} props.prepareProductData - Function to prepare and format product data
 * @param {boolean} props.isOutOfStock - Whether the product is out of stock
 * @param {Object} props.customizations - Object containing all customization options
 * @param {Object} props.product - The product data with all necessary information
 * @param {Function} props.trackAddToCart - Optional function to track add to cart events
 * @param {string} props.buttonText - Custom button text (defaults to "Add to Cart")
 * @param {string} props.outOfStockText - Custom text for out of stock (defaults to "Unavailable")
 * @param {string} props.className - Additional CSS classes
 */
const AddToCart = ({
  productId,
  prepareProductData,
  isOutOfStock,
  customizations = {},
  product,
  trackAddToCart,
  buttonText = "Add to Cart",
  outOfStockText = "Unavailable",
  className = "",
}) => {
  // Add to cart handler
  const handleAddToCart = useCallback(async () => {
    if (isOutOfStock) {
      toast.error(`This ${product?.productType || 'product'} is currently out of stock`);
      return;
    }

    // Validate required customizations
    if (product?.Size?.length > 0 && !customizations.size) {
      toast.error("Please select a size");
      return;
    }
    
    if (product?.Color?.length > 0 && !customizations.color) {
      toast.error("Please select a color");
      return;
    }

    try {
      // Track the event if tracking function is provided
      if (typeof trackAddToCart === 'function') {
        trackAddToCart();
      }

      const productData = await prepareProductData();
      if (!productData) return;

      const cart = JSON.parse(localStorage.getItem("cart") || "[]");
      const existingItemIndex = cart.findIndex((item) => item.id === productId);

      if (existingItemIndex !== -1) {
        toast.info(`${product?.productType || 'Product'} is already in the cart!`);
      } else {
        cart.push(productData);
        localStorage.setItem("cart", JSON.stringify(cart));
        toast.success(`${product?.productType || 'Product'} added to cart!`);
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error(`Failed to add ${product?.productType || 'product'} to cart`);
    }
  }, [
    isOutOfStock,
    customizations,
    product,
    prepareProductData,
    trackAddToCart,
    productId
  ]);

  return (
    <button
      onClick={handleAddToCart}
      disabled={isOutOfStock}
      className={`w-full py-2.5 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl 
                 font-semibold text-base md:text-lg transition-all duration-300 
                 flex items-center justify-center shadow-md hover:shadow-lg ${
                   isOutOfStock
                     ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                     : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                 } ${className}`}
    >
      <ShoppingCart className="w-4 md:w-5 h-4 md:h-5 mr-2" />
      {isOutOfStock ? outOfStockText : buttonText}
    </button>
  );
};

export default AddToCart;